import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Flower2, Pencil, UserRound, X } from 'lucide-react';
import { getProductImageUrl } from '../../utils/productImage';
import apiService from '../../services/api';
import RdvTerminerModal from '../rendezVous/RdvTerminerModal';
import { formatDurationLabel, formatPeopleLabel } from '../rendezVous/RdvDetailContent';
import { mapRdvToCommande, RDV_STATUS_UX } from '../../utils/equipeRdvRules';
import {
  canRefundRendezVous,
  formatEuro,
  getRdvPaymentView,
  getRdvSoldeRemboursable,
} from '../../utils/rdvPaymentDisplay';
import { resolveRdvPaymentTone, rdvPaymentToneClassName } from '../../utils/rdvPaymentTone';
import { buildRdvPricingRecap } from '../../utils/priceTax';
import { getRdvNumeroDisplay } from '../../utils/rdvStatusUx';
import {
  clientDisplayName,
  formatParisClock,
  formatParisDayTitle,
  intervenantDisplayName,
  parisYmd,
  rdvDurationMinutes,
} from '../../utils/planningDayParis';
import { avatarClass, memberInitials, memberRole } from './planningPeople';

const ACTIONS = {
  EN_ATTENTE: [
    { status: 'CONFIRME', label: 'Confirmer', className: 'bg-violet-600 text-white hover:bg-violet-700' },
    { status: 'ANNULE', label: 'Annuler', className: 'bg-red-600 text-white hover:bg-red-700' },
  ],
  CONFIRME: [
    { status: 'TERMINE', label: 'Terminer', className: 'bg-emerald-600 text-white hover:bg-emerald-700' },
    { status: 'ABSENT', label: 'Client absent', className: 'bg-amber-100 text-amber-800 hover:bg-amber-200' },
    { status: 'ANNULE', label: 'Annuler', className: 'bg-red-600 text-white hover:bg-red-700' },
  ],
};

function confirmCopy(status, numero) {
  if (status === 'ANNULE') return `Annuler le rendez-vous ${numero} ?`;
  if (status === 'ABSENT') return 'Marquer ce rendez-vous comme client absent ?';
  if (status === 'CONFIRME') return `Confirmer le rendez-vous ${numero} ?`;
  return `Mettre à jour le rendez-vous ${numero} ?`;
}

function paymentHeadline(pay) {
  if (!pay) return { title: 'Non spécifié', detail: null };
  const title = pay.summaryLabel || pay.modeLabel || 'Non spécifié';
  if (pay.isRefunded) {
    return { title, detail: `Encaissé ${formatEuro(pay.amountPaid)}` };
  }
  if (pay.amountPaid > 0 && pay.amountDueLater <= 0.009) {
    return {
      title,
      detail: `${formatEuro(pay.total || pay.amountPaid)} TTC${pay.isOnline ? ' · Stripe' : ''}`,
    };
  }
  if (pay.amountPaid > 0 && pay.amountDueLater > 0) {
    return {
      title,
      detail: `Payé ${formatEuro(pay.amountPaid)} · Reste ${formatEuro(pay.amountDueLater)}`,
    };
  }
  return { title, detail: null };
}

function mixWithWhite(hex, amount = 0.78) {
  const raw = String(hex || '').replace('#', '');
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const n = parseInt(raw, 16);
  const mix = (c) => Math.round(c + (255 - c) * amount);
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
}

function prestationPhotoUrl(prestation) {
  return getProductImageUrl(prestation, { width: 180 }) || null;
}

const CLOSED_ASSIGN_STATUTS = new Set(['TERMINE', 'ANNULE', 'ABSENT']);

function assignErrorMessage(err) {
  const code = err?.code || err?.data?.code;
  if (code === 'PRACTITIONER_NOT_COMPATIBLE') {
    return 'Ce professionnel ne peut pas réaliser cette prestation.';
  }
  if (code === 'INSTRUCTEUR_HORS_COMMERCE') {
    return 'Ce professionnel n\'appartient pas à cette boutique.';
  }
  if (code === 'STATUT_RDV_CLOS') {
    return 'Ce rendez-vous ne peut plus être affecté.';
  }
  if (code === 'PRESTATION_PRACTITIONERS_NOT_CONFIGURED') {
    return 'Aucun professionnel n\'est configuré pour cette prestation.';
  }
  if (code === 'ASSIGN_INSTRUCTEUR_EXPLICITE_IMPOSSIBLE' || code === 'SLOT_NO_LONGER_AVAILABLE') {
    return 'Ce professionnel n\'est plus disponible sur ce créneau.';
  }
  if (err?.status === 403 || code === 'ACCES_REFUSE') {
    return 'Accès refusé.';
  }
  return 'Affectation impossible.';
}

function Field({ label, quiet = false, children }) {
  return (
    <div className="min-w-0">
      <p className={`text-[11px] font-medium ${quiet ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
      <div className={`mt-1 ${quiet ? 'text-sm font-medium text-gray-700' : 'text-sm font-semibold text-gray-900'}`}>
        {children}
      </div>
    </div>
  );
}

/**
 * Panneau latéral détail RDV.
 * Présentation uniquement — mêmes actions que la version précédente.
 */
export default function PlanningRdvDrawer({ rdv, member, commercantId, onClose, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [terminerOpen, setTerminerOpen] = useState(false);
  const [techOpen, setTechOpen] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [prestaPhoto, setPrestaPhoto] = useState(() => prestationPhotoUrl(rdv?.prestation));
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [staffEditOpen, setStaffEditOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  useEffect(() => {
    setError('');
    setPendingStatus(null);
    setTechOpen(false);
    setImageFailed(false);
    setAssignError('');
    setAssigning(false);
    setStaffEditOpen(false);
    setStaffOptions([]);
    setStaffLoaded(false);
  }, [rdv?.id]);

  const staffEditable = Boolean(
    rdv
    && !CLOSED_ASSIGN_STATUTS.has(String(rdv.statut || '').toUpperCase()),
  );

  const loadAssignableStaff = useCallback(() => {
    if (!staffEditable || !rdv?.id || !rdv?.prestation?.id) {
      setStaffOptions([]);
      setStaffLoaded(true);
      return Promise.resolve();
    }
    setStaffLoaded(false);
    return apiService.getRendezVousAssignableInstructeurs(rdv.id)
      .then((response) => {
        const list = [...(response?.instructeurs || [])];
        const currentId = rdv.instructeurId || response?.instructeurActuelId;
        if (currentId && !list.some((item) => item.id === currentId)) {
          const cur = rdv.instructeur || member;
          if (cur?.id === currentId) {
            list.unshift({
              id: cur.id,
              prenom: cur.prenom,
              nom: cur.nom,
              email: cur.email,
              fonctionMetier: cur.fonctionMetier,
            });
          }
        }
        list.sort((a, b) => {
          const la = `${a.prenom || ''} ${a.nom || ''}`.trim().toLocaleLowerCase('fr');
          const lb = `${b.prenom || ''} ${b.nom || ''}`.trim().toLocaleLowerCase('fr');
          return la.localeCompare(lb, 'fr');
        });
        setStaffOptions(list);
        setStaffLoaded(true);
      })
      .catch(() => {
        const fallback = [];
        const currentId = rdv.instructeurId;
        const cur = rdv.instructeur || member;
        if (currentId && cur?.id === currentId) {
          fallback.push({
            id: cur.id,
            prenom: cur.prenom,
            nom: cur.nom,
            email: cur.email,
            fonctionMetier: cur.fonctionMetier,
          });
        }
        setStaffOptions(fallback);
        setStaffLoaded(true);
        setAssignError('Impossible de charger les professionnels disponibles.');
      });
  }, [staffEditable, rdv?.id, rdv?.prestation?.id, rdv?.instructeurId, rdv?.instructeur, member]);

  useEffect(() => {
    const inline = prestationPhotoUrl(rdv?.prestation);
    if (inline) {
      setPrestaPhoto(inline);
      return undefined;
    }
    const prestationId = rdv?.prestation?.id;
    if (!prestationId) {
      setPrestaPhoto(null);
      return undefined;
    }
    let cancelled = false;
    setPrestaPhoto(undefined);
    apiService.getPrestationById(prestationId)
      .then((response) => {
        if (cancelled) return;
        setPrestaPhoto(prestationPhotoUrl(response?.prestation) || null);
      })
      .catch(() => {
        if (!cancelled) setPrestaPhoto(null);
      });
    return () => {
      cancelled = true;
    };
  }, [rdv?.id, rdv?.prestation?.id, rdv?.prestation?.images]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!rdv) return null;

  const commande = mapRdvToCommande(rdv);
  const statut = String(rdv.statut || '').toUpperCase();
  const statusMeta = RDV_STATUS_UX[statut] || { label: statut || '—', className: 'bg-gray-100 text-gray-700' };
  const numero = getRdvNumeroDisplay(commande);
  const recap = buildRdvPricingRecap(commande);
  const pay = getRdvPaymentView(commande);
  const paymentTone = resolveRdvPaymentTone(commande);
  const payLine = paymentHeadline(pay);
  const client = rdv.user;
  const clientName = clientDisplayName(client);
  const pro = member || rdv.instructeur;
  const proName = pro ? intervenantDisplayName(pro) : 'Non assigné';
  const currentStaffId = rdv.instructeurId || '';
  const otherAssignableCount = staffOptions.filter((item) => item.id !== currentStaffId).length;
  const duration = formatDurationLabel(rdvDurationMinutes(rdv)) || '—';
  const ressourceLabel = rdv.ressource?.nom?.trim() || null;
  const note = String(rdv.notes || '').trim();
  const actions = ACTIONS[statut] || [];
  const canRefund = canRefundRendezVous(commande);
  const refundSolde = getRdvSoldeRemboursable(commande);
  const dayTitle = rdv.dateDebut ? formatParisDayTitle(parisYmd(new Date(rdv.dateDebut))) : '—';
  const clock = rdv.dateDebut ? formatParisClock(rdv.dateDebut) : '—';
  const prestationNom = rdv.prestation?.nom?.trim() || 'Prestation';
  const photoSrc = prestaPhoto && !imageFailed ? prestaPhoto : null;
  const photoPending = prestaPhoto === undefined && !imageFailed;
  const wash = mixWithWhite(rdv.prestation?.couleur, 0.84) || '#F6F1FF';
  const accent = rdv.prestation?.couleur || '#7C3AED';

  function openStaffEdit() {
    setAssignError('');
    setStaffEditOpen(true);
    loadAssignableStaff();
  }

  function cancelStaffEdit() {
    setStaffEditOpen(false);
    setAssignError('');
  }

  async function assignProfessional(instructeurId) {
    if (!instructeurId || assigning) return;
    if (rdv.instructeurId && rdv.instructeurId === instructeurId) {
      cancelStaffEdit();
      return;
    }
    setAssigning(true);
    setAssignError('');
    try {
      const response = await apiService.assignRendezVousInstructeur(rdv.id, instructeurId);
      const next = response?.rendezVous;
      if (!next?.instructeurId) {
        setAssignError('Affectation impossible.');
        return;
      }
      setStaffEditOpen(false);
      onUpdated?.(next);
    } catch (err) {
      setAssignError(assignErrorMessage(err));
      const code = err?.code || err?.data?.code;
      if (code === 'SLOT_NO_LONGER_AVAILABLE' || code === 'ASSIGN_INSTRUCTEUR_EXPLICITE_IMPOSSIBLE') {
        await loadAssignableStaff();
      }
    } finally {
      setAssigning(false);
    }
  }

  async function applyStatus(status) {
    setBusy(true);
    setError('');
    try {
      await apiService.updateRendezVous(rdv.id, { statut: status });
      setPendingStatus(null);
      onUpdated?.({ ...rdv, statut: status });
    } catch (err) {
      setError(err?.data?.error?.message || err?.message || 'Mise à jour impossible');
    } finally {
      setBusy(false);
    }
  }

  async function applyRefund() {
    setBusy(true);
    setError('');
    try {
      await apiService.rembourserRendezVous(rdv.id, { type: 'COMPLET' });
      setPendingStatus(null);
      onUpdated?.(rdv);
    } catch (err) {
      setError(err?.data?.error?.message || err?.message || 'Remboursement impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-slate-900/15"
        aria-label="Fermer le rendez-vous"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Rendez-vous"
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] w-full flex-col rounded-t-3xl border border-gray-100 bg-white shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-[540px] sm:rounded-none sm:border-y-0 sm:border-r-0"
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-gray-200 sm:hidden" />
        <header className="flex items-start gap-3 px-5 pb-3 pt-4 sm:pt-5">
          <h2 className="min-w-0 flex-1 text-lg font-bold leading-snug text-gray-900">
            Rendez-vous
          </h2>
          <span className={`mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusMeta.className}`}>
            {statusMeta.label}
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-700"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 pb-5">
          <section
            className="rounded-3xl px-4 py-4"
            style={{ background: `linear-gradient(145deg, ${wash} 0%, #fffdfb 78%)` }}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt=""
                    onError={() => setImageFailed(true)}
                    className="h-20 w-20 shrink-0 rounded-2xl object-cover shadow-sm"
                  />
                ) : (
                  <span
                    className="inline-flex h-20 w-20 shrink-0 items-center justify-center rounded-2xl shadow-sm"
                    style={{ backgroundColor: mixWithWhite(accent, 0.62) || '#EDE4FF', color: accent }}
                    aria-hidden
                  >
                    {photoPending ? null : <Flower2 className="h-7 w-7" strokeWidth={1.5} />}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate text-base font-semibold text-gray-900">{prestationNom}</p>
                  <p className="mt-0.5 text-sm text-gray-500">{duration}</p>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="text-sm text-gray-500">{dayTitle}</p>
                <p className="mt-1 text-[2rem] font-bold leading-none tracking-tight text-gray-900">{clock}</p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3 border-t border-white/80 pt-4">
              <span
                className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarClass(client?.id || clientName)}`}
              >
                {memberInitials(client || { prenom: clientName })}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-bold text-gray-900">{clientName}</p>
                <p className="truncate text-xs text-gray-500">{client?.telephone || 'Téléphone non renseigné'}</p>
                <p className="truncate text-xs text-gray-500">{client?.email || 'Email non renseigné'}</p>
              </div>
              {client?.id ? (
                <Link
                  to={`/clients/${client.id}`}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-violet-700 shadow-sm hover:bg-violet-100"
                >
                  <UserRound className="h-3.5 w-3.5" />
                  Voir le profil client
                </Link>
              ) : null}
            </div>
          </section>

          <section className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl bg-gray-50/80 px-3.5 py-3">
              <Field label="Professionnel">
                {staffEditable && staffEditOpen ? (
                  <span className="block space-y-2">
                    <select
                      aria-label="Choisir un professionnel"
                      value={currentStaffId}
                      disabled={assigning || !staffLoaded}
                      onChange={(event) => assignProfessional(event.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-sm font-semibold text-gray-900"
                    >
                      {!currentStaffId ? (
                        <option value="">{assigning ? 'Affectation…' : 'Choisir un professionnel'}</option>
                      ) : null}
                      {staffOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {[item.prenom, item.nom].filter(Boolean).join(' ')}
                        </option>
                      ))}
                    </select>
                    {!staffLoaded ? (
                      <span className="block text-xs font-medium text-gray-400">Chargement des professionnels…</span>
                    ) : null}
                    {staffLoaded && !assigning && currentStaffId && otherAssignableCount === 0 ? (
                      <span className="block text-xs font-medium text-gray-500">
                        Aucun autre professionnel disponible sur ce créneau.
                      </span>
                    ) : null}
                    {staffLoaded && !currentStaffId && staffOptions.length === 0 ? (
                      <span className="block text-xs font-medium text-gray-500">
                        Aucun professionnel disponible sur ce créneau.
                      </span>
                    ) : null}
                    {assignError ? (
                      <span className="block text-xs font-medium text-rose-700">{assignError}</span>
                    ) : null}
                    <button
                      type="button"
                      onClick={cancelStaffEdit}
                      disabled={assigning}
                      className="text-xs font-semibold text-gray-500 hover:text-gray-800"
                    >
                      Annuler
                    </button>
                  </span>
                ) : (
                  <span className="flex items-start justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${avatarClass(rdv.instructeurId || 'none')}`}
                      >
                        {pro ? memberInitials(pro) : '—'}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate">{proName}</span>
                        <span className="block truncate text-[11px] font-medium text-gray-400">
                          {pro ? memberRole(pro) : 'Non assigné'}
                        </span>
                      </span>
                    </span>
                    {staffEditable ? (
                      <button
                        type="button"
                        onClick={openStaffEdit}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-violet-700 hover:bg-violet-50"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                        {rdv.instructeurId ? 'Modifier' : 'Affecter'}
                      </button>
                    ) : null}
                  </span>
                )}
              </Field>
            </div>
            <div
              className={`rounded-2xl border px-3.5 py-3 ${rdvPaymentToneClassName('panel', paymentTone)}`}
            >
              <Field label="Paiement">
                <span className="block text-[15px]">{payLine.title}</span>
                {payLine.detail ? (
                  <span className="mt-0.5 block text-xs font-medium text-gray-600">{payLine.detail}</span>
                ) : null}
              </Field>
            </div>
          </section>

          <section className="rounded-2xl border border-gray-100 bg-slate-50/70 px-3.5 py-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <Field quiet label="Espace / lieu">
                {ressourceLabel || 'Non renseigné'}
              </Field>
              <Field quiet label="Participants">{formatPeopleLabel(rdv.participants)}</Field>
            </div>
          </section>

          {note ? (
            <section className="rounded-2xl border border-amber-100/90 bg-amber-50/50 px-3.5 py-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-900/90">
                Note client / particularité
              </p>
              <p className="mt-2 text-sm font-medium leading-relaxed text-gray-900 whitespace-pre-wrap">
                {note}
              </p>
            </section>
          ) : null}

          <section className="border-t border-gray-200 px-1 pt-4">
            <h3 className="text-sm font-semibold text-gray-900">Récapitulatif tarifaire</h3>
            <div className="mt-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-gray-800">{prestationNom}</p>
                <p className="text-xs text-gray-400">
                  {recap.participants} × {formatEuro(recap.unitTTC)} TTC (TVA {recap.defaultTvaRate}%)
                </p>
              </div>
              <p className="text-sm font-semibold text-gray-900">{formatEuro(recap.prestation.ttc)} TTC</p>
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-gray-500">
              <div className="flex justify-between">
                <span>Dont HT</span>
                <span>{formatEuro(recap.totalHT)}</span>
              </div>
              <div className="flex justify-between">
                <span>Dont TVA</span>
                <span>{formatEuro(recap.totalTVA)}</span>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl bg-amber-50 px-3 py-3">
              <span className="text-sm font-bold uppercase tracking-wide text-gray-900">Total TTC</span>
              <span className="text-lg font-bold text-gray-900">{formatEuro(recap.montantAPayer)}</span>
            </div>
          </section>

          <section>
            <button
              type="button"
              onClick={() => setTechOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-xl px-1 py-2 text-left text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Informations comptables et techniques
              <ChevronDown className={`h-4 w-4 text-gray-400 transition ${techOpen ? 'rotate-180' : ''}`} />
            </button>
            {techOpen ? (
              <dl className="mt-1 space-y-2 px-1 pb-2 text-xs text-gray-500">
                <div className="flex justify-between gap-3">
                  <dt>N° rendez-vous</dt>
                  <dd className="truncate font-mono text-gray-700">{numero}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Identifiant</dt>
                  <dd className="truncate font-mono text-gray-700">{rdv.id}</dd>
                </div>
                {rdv.paiementIntentId ? (
                  <div className="flex justify-between gap-3">
                    <dt>Référence paiement</dt>
                    <dd className="truncate font-mono text-gray-700">{rdv.paiementIntentId}</dd>
                  </div>
                ) : null}
                <div className="flex justify-between gap-3">
                  <dt>Mode</dt>
                  <dd className="text-gray-700">{pay?.modeLabel || rdv.modePaiement || '—'}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Payé</dt>
                  <dd className="text-gray-700">{formatEuro(pay?.amountPaid || 0)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt>Reste</dt>
                  <dd className="text-gray-700">{formatEuro(pay?.amountDueLater || 0)}</dd>
                </div>
                {rdv.notesCommercant ? (
                  <div>
                    <dt>Note interne</dt>
                    <dd className="mt-1 whitespace-pre-wrap text-gray-700">{rdv.notesCommercant}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
          </section>
        </div>

        <footer className="border-t border-gray-100 bg-white px-5 py-4">
          {error ? <p className="mb-2 text-xs text-red-600">{error}</p> : null}
          {pendingStatus ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-700">{confirmCopy(pendingStatus, numero)}</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => (pendingStatus === 'REFUND' ? applyRefund() : applyStatus(pendingStatus))}
                  className="rounded-xl bg-violet-600 px-3 py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {busy ? '…' : 'Confirmer'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setPendingStatus(null)}
                  className="rounded-xl border border-gray-200 px-3 py-3 text-sm font-semibold text-gray-700"
                >
                  Retour
                </button>
              </div>
            </div>
          ) : (
            <>
              {actions.length > 0 ? (
                <div className={`grid gap-2 ${actions.length > 2 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                  {actions.map((action) => (
                    <button
                      key={action.status}
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        if (action.status === 'TERMINE') setTerminerOpen(true);
                        else setPendingStatus(action.status);
                      }}
                      className={`rounded-xl px-2 py-3 text-sm font-semibold disabled:opacity-60 ${action.className}`}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">Aucune action disponible pour ce rendez-vous.</p>
              )}
              {canRefund ? (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setPendingStatus('REFUND')}
                  className="mt-2 w-full rounded-xl bg-orange-500 px-3 py-3 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-60"
                >
                  Rembourser (Stripe) — solde {formatEuro(refundSolde)}
                </button>
              ) : null}
            </>
          )}
        </footer>
      </aside>

      <RdvTerminerModal
        open={terminerOpen}
        commande={commande}
        commercantId={commercantId}
        formatEuro={formatEuro}
        onClose={() => setTerminerOpen(false)}
        onSuccess={() => {
          setTerminerOpen(false);
          onUpdated?.({ ...rdv, statut: 'TERMINE' });
        }}
      />
    </>
  );
}
